
import { useState, useEffect, useCallback } from 'react';
import { Variation } from '@/components/admin/locations/types/variation';

/**
 * Hook to manage a cache of colorScheme values by variation key
 * This ensures consistent color mapping across all components
 */
export const useColorSchemeCache = (variations: Variation[] = []) => {
  const [colorSchemeMap, setColorSchemeMap] = useState<Record<string, string>>({});
  
  // Default colorScheme values for common keys
  const defaultColors: Record<string, string> = {
    default: 'blue',
    preschool: 'purple',
    elementary: 'green',
    middleschool: 'orange',
    highschool: 'red',
    afterschool: 'teal',
    enrichment: 'indigo',
    academic: 'cyan',
    arts: 'pink',
    sports: 'yellow',
    // Pattern-based keys
    pre: 'purple',
    elem: 'green',
    middle: 'orange',
    high: 'red',
    teen: 'pink',
    sport: 'yellow',
    art: 'pink',
    science: 'teal',
    music: 'indigo',
    dance: 'purple',
    stem: 'cyan'
  };
  
  // Initialize or update the cache when variations change
  useEffect(() => {
    // Create a new map that includes both defaults and actual values
    const newMap: Record<string, string> = { ...defaultColors };
    
    // Add variations from the provided list, using index-based colors
    if (variations && variations.length > 0) {
      const colors = ['blue', 'green', 'purple', 'orange', 'teal', 'pink', 'red', 'yellow', 'indigo', 'cyan'];
      
      variations.forEach((variation, index) => {
        if (variation.key) {
          // Always prioritize explicit colorScheme from variation
          if (variation.colorScheme) {
            newMap[variation.key] = variation.colorScheme;
          } else {
            // Use index-based color assignment for consistent colors
            newMap[variation.key] = colors[index % colors.length];
          }
        }
      });
    }
    
    setColorSchemeMap(newMap);
  }, [variations]);
  
  /**
   * Core algorithm for deterministic color assignment (fallback)
   * This is the source of truth for color assignment used across the app
   */
  const getHashForVariationKey = useCallback((key: string, length: number): number => {
    return key.split('').reduce((acc, char) => acc * 7 + char.charCodeAt(0) * 31 + 5, 0) % length;
  }, []);
  
  /**
   * Get color by index for consistent program colors
   */
  const getColorByIndex = useCallback((index: number): string => {
    const colors = ['blue', 'green', 'purple', 'orange', 'teal', 'pink', 'red', 'yellow', 'indigo', 'cyan'];
    return colors[index % colors.length];
  }, []);
  
  // Get color for a specific variation key
  const getColorForVariation = useCallback((variationKey: string): string => {
    if (!variationKey) {
      return 'blue'; // Default fallback
    }
    
    // First check our cache
    const color = colorSchemeMap[variationKey];
    if (color) {
      return color;
    }
    
    // Check for substring matches
    for (const [key, value] of Object.entries(defaultColors)) {
      if (variationKey.toLowerCase().includes(key.toLowerCase())) {
        return value;
      }
    }
    
    // Final fallback - use the definitive deterministic algorithm
    const colors = ['blue', 'green', 'purple', 'orange', 'teal', 'pink', 'red', 'yellow', 'indigo', 'cyan'];
    const hashIndex = getHashForVariationKey(variationKey, colors.length);
    return colors[hashIndex];
  }, [colorSchemeMap, defaultColors, getHashForVariationKey]);
  
  return {
    colorSchemeMap,
    getColorForVariation,
    getHashForVariationKey,
    getColorByIndex
  };
};
